import axios from 'axios';
import { getAuthToken, removeAuthToken } from '../utils/authToken';

// Use the API URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
  }
});

// Add a request interceptor to include the authentication token in all requests
axiosInstance.interceptors.request.use(
  (config) => {
    // Get the token from localStorage
    const token = getAuthToken();

    // If the token exists, add it to the Authorization header
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle authentication errors
axiosInstance.interceptors.response.use(
  (response) => {
    // Return successful responses as-is
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized responses
    if (error.response && error.response.status === 401) {
      removeAuthToken();

      // Only redirect if we're not already on the login page and not making auth-related requests
      const isAuthRequest = error.config.url.includes('/auth/');
      const isOnLoginPage = window.location.pathname === '/';

      if (!isAuthRequest && !isOnLoginPage) {
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

// Create the api object with all methods
const api = {
  get: async (url, config = {}) => {
    try {
      const response = await axiosInstance.get(url, config);
      return response;
    } catch (error) {
      throw error;
    }
  },

  post: async (url, data) => {
    try {
      const response = await axiosInstance.post(url, data);
      return response;
    } catch (error) {
      throw error;
    }
  },

  put: async (url, data) => {
    try {
      const response = await axiosInstance.put(url, data);
      return response;
    } catch (error) {
      throw error;
    }
  },

  delete: async (url) => {
    try {
      const response = await axiosInstance.delete(url);
      return response;
    } catch (error) {
      console.error('API Delete Error:', error);
      throw error;
    }
  },

  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axiosInstance.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      window.location.href = '/export';
      return response.data;
    } catch (error) {
      console.error('Upload Error:', error);
      if (error.response) {
        throw new Error(error.response.data.detail || 'Upload failed');
      } else if (error.request) {
        throw new Error('Network error - please check your connection');
      } else {
        throw new Error('Upload failed - please try again');
      }
    }
  },

  cleanup: async (uploadId) => {
    try {
      await axiosInstance.delete(`/upload/status/${uploadId}`);
    } catch (error) {
      // Cleanup error - silently fail
    }
  }
};

export const fetchExportData = async () => {
  try {
    // Make sure we're using the correct path with '/api'
    const response = await axiosInstance.get('/api/export/data');
    return response.data;
  } catch (error) {
    console.error('Error fetching export data:', error);
    throw error;
  }
};

export const deleteDeliveryNote = async (documentId, collectionName = null) => {
  try {
    let endpoint = `/api/delivery-notes/document/${documentId}`;
    if (collectionName) {
      endpoint += `?collection=${encodeURIComponent(collectionName)}`;
    }

    const response = await axiosInstance.delete(endpoint);
    return response.data;
  } catch (error) {
    console.error('Error deleting delivery note:', error);
    throw error;
  }
};

export default api;